import { Fragment, FunctionComponent, useEffect } from "react";
import { Button } from "@App/Components/Funnel/Button";
import { useTranslation } from "react-i18next";
import { State } from "@App/state";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import {
  CREATE_DELIVERY_MUTATION,
  CreateDeliveryMutation,
} from "@App/graphql/mutations/fleetManagement/delivery/createDelivery";
import { LoadingIcon } from "@App/Components/common/Icons";
import { FormErrors } from "@App/types/enums";
import { formatDate } from "@App/utils/dates";
import { Headline } from "@App/Components/atoms/common/Headline";

export type DeliverySubmitProps = {
  onSubmit: () => void;
};
export const DeliverySubmit: FunctionComponent<DeliverySubmitProps> = ({
  onSubmit,
}: DeliverySubmitProps) => {
  const { t } = useTranslation(); // i18next

  const navigate = useNavigate();

  const organizationId = useSelector((state: State) => state.orders.organization.id);
  const contractDetails = useSelector((state: State) => state.orders.contractDetails);
  const date1 = useSelector((state: State) => state.orders.deliveryDates.date1);
  const date2 = useSelector((state: State) => state.orders.deliveryDates.date2);
  const date3 = useSelector((state: State) => state.orders.deliveryDates.date3);

  const [createDelivery, { loading, data, error }] =
    useMutation<CreateDeliveryMutation>(CREATE_DELIVERY_MUTATION);

  const submit = async () => {
    await createDelivery({
      variables: {
        organizationId,
        carId: contractDetails.carId,
        name: contractDetails.name,
        street: contractDetails.street,
        postcode: contractDetails.postcode,
        city: contractDetails.city,
        phone: contractDetails.phone,
        date1: formatDate(date1, "yyyy-MM-dd"),
        date2: formatDate(date2, "yyyy-MM-dd"),
        date3: formatDate(date3, "yyyy-MM-dd"),
      },
    });
  };

  useEffect(() => {
    if (!loading && !error && data) {
      onSubmit();
    }
  }, [loading, error, data]);

  // // redirect back to start if parameters are missing
  useEffect(() => {
    if (!contractDetails || !date1 || !date2 || !date3) {
      navigate("/partner/mobility-concept/fahrzeuglieferung");
    }
  }, []);

  return (
    <Fragment>
      <div
        className={
          "container md:w-[600px] m-5 h-[calc(100vh-150px)] flex items-start md:items-center mx-auto"
        }
      >
        <div>
          <Headline type="h1">{t("vehicleDelivery.submit-headline")}</Headline>
          <div className="font-bold">{t("vehicleDelivery.submit-identity-headline")}</div>
          <div className="text-neutral-500 mb-3">{contractDetails.agreementNumber}</div>

          <div className="flex flex-col md:flex-row mb-3">
            <div className="flex-1">
              <div className="font-bold">{t("vehicleDelivery.submit-address-headline")}</div>
              <div className="text-neutral-500 mb-3">
                {contractDetails.name}
                <br />
                {contractDetails.phone}
                <br />
                {contractDetails.street}
                <br />
                {contractDetails.postcode} {contractDetails.city}
              </div>
            </div>
            <div className="flex-1">
              <div className="font-bold">{t("vehicleDelivery.submit-date-headline")}</div>
              <div className="text-neutral-500 mb-3">
                1. {date1 && format(date1, "iiii - dd.MM.yyyy", { locale: de })}
                <br />
                2. {date2 && format(date2, "iiii - dd.MM.yyyy", { locale: de })}
                <br />
                3. {date3 && format(date3, "iiii - dd.MM.yyyy", { locale: de })}
              </div>
            </div>
          </div>

          <p
            className={"text-neutral-500 mb-3"}
            dangerouslySetInnerHTML={{ __html: t("vehicleDelivery.submit-text") }}
          ></p>

          {error?.message === FormErrors.DELIVERY_WISH_NOT_SET && (
            <div className={"text-error-500"}>
              <p>{t("vehicleDelivery.submit-error")}</p>
            </div>
          )}

          <Button
            title={t("vehicleDelivery.submit-button")}
            className={"md:!w-1/2"}
            onClick={submit}
            disabled={loading}
          />
          {loading && <LoadingIcon className="inline animate-spin h-5 w-5 ml-3" />}
        </div>
      </div>
    </Fragment>
  );
};
